import { clientStorage } from "@/lib/clientStorage";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { 
  Calendar, Search, Plus, Trash2, Edit2, AlertTriangle,
  Printer, FileSpreadsheet, Save, X, Upload, Loader2, FolderOpen,
  Cloud, CheckCircle2, Download
} from "lucide-react";
import { toast } from "sonner";
import { usePortalFocus, useScrollToFocus } from "@/hooks/usePortalFocus";
import { parseValsDiaryFileStandalone } from "@/lib/standaloneServices";
import {
  mapParsedDiaryEntries,
  normalizeValsDiaryEntry,
  VALS_FUNDING_STATUSES,
  valsDiaryDaySortValue,
} from "@/lib/valsDiary";
import { useCloudDrafts } from "@/hooks/useCloudDrafts";
import { diaryReminders } from "@/lib/grantReminders";
import GrantRemindersPanel from "@/components/GrantRemindersPanel";
import { DiaryCodeNote, DiaryCodesLegend } from "@/components/DiaryCodes";
import { getPortalToolPath } from "@/lib/portalToolRoutes";
import { DIARY_CODES } from "@/lib/diaryCodes";
import { listLocalMatters, syncMattersWithPortal } from "@/lib/aidPlannerStore";
import { fillValsDiaryFromAidPlanner, valsDiarySuggestions, valsFillFields } from "@/lib/diaryAidMatch";
import { getClaimAvailabilityTable } from "@/lib/aidPlannerCriminalFees";

// What the Aid Planner says about a diary row: filled from it, a different status to the one
// chosen, or more than one matter with this name.
function AidPlannerHint({ entry, suggestion, onUse }) {
  if (suggestion?.ambiguous) {
    return <p className="no-print mt-1 text-[9px] font-semibold text-amber-800">{suggestion.ambiguous} Aid Planner matters have this name. Check which one.</p>;
  }
  if (!suggestion?.status) return null;
  if (entry.aidPlannerFill && entry.aidStatus === suggestion.status) {
    return <p title={suggestion.reason} className="no-print mt-1 text-[9px] font-semibold text-teal-800">Filled from the Aid Planner ({suggestion.matterName}). Check and confirm.</p>;
  }
  if (entry.aidStatus === suggestion.status) return null;
  return (
    <p className="no-print mt-1 text-[9px] text-slate-600">
      Aid Planner says: <strong>{suggestion.status}</strong>{suggestion.aidNumber && suggestion.status === "Aid in place" ? ` (${suggestion.aidNumber})` : ""}.{" "}
      <button type="button" onClick={() => onUse(suggestion)} className="font-bold text-teal-700 hover:underline">Use this</button>
    </p>
  );
}
import {
  VALS_DIARY_FORM_TYPE,
  pickLatestWeek,
  readWeekArchive,
  readWeekFile,
  weekArchive,
  weekNameFor,
  weekSummaries,
  weekTooLarge,
} from "@/lib/valsDiaryWeeks";
import { diaryFirm } from "@/lib/diaryFirms";

const INITIAL_ENTRIES = [
  // Monday 13
  { id: "1", date: "Monday 13", court: "Ballarat Magistrates’ Court", clientName: "Lydia LAUCH", appearanceType: "FVCD", solicitor: "LF", notes: "LF sought admin adj.", aidStatus: "Aid in place", aidNumber: "VLA-9921", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "2", date: "Monday 13", court: "Colac Magistrates’ Court", clientName: "", appearanceType: "VALS Date", solicitor: "DE", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "3", date: "Monday 13", court: "Horsham Children’s Court", clientName: "John CLAPHAM", appearanceType: "M", solicitor: "JO", notes: "JO to appear via Webex", aidStatus: "Aid in place", aidNumber: "VLA-2201", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "4", date: "Monday 13", court: "Kyneton Magistrates’ Court", clientName: "", appearanceType: "VALS Date", solicitor: "GJ", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "5", date: "Monday 13", court: "Melbourne Koori Court", clientName: "Shaun GALE", appearanceType: "HATISLOW (M)", solicitor: "LF", notes: "Naomi Newbound briefed for HATISLOW", aidStatus: "Aid in place", aidNumber: "VLA-4418", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "6", date: "Monday 13", court: "Melbourne Magistrates’ Court", clientName: "Chelsea JOHNSTONE", appearanceType: "M", solicitor: "TWB", notes: "", aidStatus: "Aid in place", aidNumber: "VLA-8819", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "7", date: "Monday 13", court: "Melbourne Magistrates’ Court", clientName: "Maruwaan HERMAN", appearanceType: "M", solicitor: "MM", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "8", date: "Monday 13", court: "Warrnambool County Court", clientName: "Jindara AUSTIN", appearanceType: "PG", solicitor: "XF", notes: "Gordon Chilsolm appearing for AUSTIN", aidStatus: "Aid in place", aidNumber: "VLA-7731", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "9", date: "Monday 13", court: "Melbourne Koori Court", clientName: "Koen RADOICOVICH", appearanceType: "PG", solicitor: "ET", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true },
  { id: "10", date: "Monday 13", court: "VCAT", clientName: "Christopher AUSTIN", appearanceType: "Firearms restriction", solicitor: "GW", notes: "PR babysitting", aidStatus: "Aid in place", aidNumber: "VLA-0992", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true },
  { id: "11", date: "Monday 13", court: "Wyndham Children’s Court", clientName: "Janice TUPUHI", appearanceType: "RoD", solicitor: "GW", notes: "HB appearing", aidStatus: "Aid in place", aidNumber: "VLA-8845", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true },
  { id: "12", date: "Monday 13", court: "Wyndham Children’s Court", clientName: "Ayub ALI", appearanceType: "M", solicitor: "ET", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true },
  
  // Tuesday 14
  { id: "13", date: "Tuesday 14", court: "Benalla Magistrates’ Court", clientName: "Izack WARREN", appearanceType: "PG - DL Assistance", solicitor: "TWB", notes: "", aidStatus: "Aid in place", aidNumber: "VLA-4412", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "14", date: "Tuesday 14", court: "Echuca Magistrates’ Court", clientName: "Jayden CHARLES", appearanceType: "M", solicitor: "GJ", notes: "Mentions", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "15", date: "Tuesday 14", court: "Echuca Magistrates’ Court", clientName: "Tyson BRIGGS", appearanceType: "M", solicitor: "GJ", notes: "Mentions", aidStatus: "Aid in place", aidNumber: "VLA-1123", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "16", date: "Tuesday 14", court: "Frankston Magistrates’ Court", clientName: "Cameron WILSON", appearanceType: "Consol PG", solicitor: "SHO", notes: "Jervis Dean briefed for WILSON", aidStatus: "Aid in place", aidNumber: "VLA-5612", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "17", date: "Tuesday 14", court: "Geelong Magistrates’ Court", clientName: "Margaret DAY", appearanceType: "FM", solicitor: "JO", notes: "JO advise no appearance required", aidStatus: "Aid in place", aidNumber: "VLA-9903", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "18", date: "Tuesday 14", court: "Melbourne County Court", clientName: "Paul MILLER", appearanceType: "A", solicitor: "PC", notes: "Julia Wallace briefed for MILLER", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: true, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "19", date: "Tuesday 14", court: "Melbourne Magistrates’ Court", clientName: "Allen WILLIAMS", appearanceType: "CME/PG", solicitor: "TWB", notes: "Naomi Newbound briefed for OAKROOT", aidStatus: "Aid in place", aidNumber: "VLA-7788", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "20", date: "Tuesday 14", court: "Melbourne Magistrates’ Court", clientName: "*Heath OAKROOT", appearanceType: "Sent", solicitor: "PC", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "21", date: "Tuesday 14", court: "Melbourne Magistrates’ Court", clientName: "Alic DRIER", appearanceType: "Arraignment", solicitor: "CMR", notes: "", aidStatus: "Aid in place", aidNumber: "VLA-9204", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "22", date: "Tuesday 14", court: "Mildura Magistrates’ Court", clientName: "Shaun PITT", appearanceType: "M", solicitor: "JM", notes: "Admin adj to 21.07.2026 for SMITTH and PITT", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "23", date: "Tuesday 14", court: "Roderick SMITH", appearanceType: "M", solicitor: "JM", notes: "Admin adj to 21.07.2026 for SMITTH and PITT", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "24", date: "Tuesday 14", court: "Moorabbin Magistrates’ Court", clientName: "Keera SHELLEY", appearanceType: "M", solicitor: "MM", notes: "MM appearing for SHELLEY", aidStatus: "Aid in place", aidNumber: "VLA-4402", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "25", date: "Tuesday 14", court: "Portland Koori Court", clientName: "Noah KNIGHT", appearanceType: "PG", solicitor: "XF", notes: "XF appearing for KNIGHT", aidStatus: "Aid in place", aidNumber: "VLA-1102", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "26", date: "Tuesday 14", court: "Sale Magistrates’ Court", clientName: "", appearanceType: "VALS Date", solicitor: "JO", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "27", date: "Tuesday 14", court: "Wyndham Magistrates’ Court", clientName: "", appearanceType: "VALS Date", solicitor: "DE", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "28", date: "Tuesday 14", court: "Echuca Children’s Court", clientName: "Dayla WESTON", appearanceType: "PG", solicitor: "DP", notes: "", aidStatus: "Aid in place", aidNumber: "VLA-8811", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true },
  { id: "29", date: "Tuesday 14", court: "Echuca Children’s Court", clientName: "Alex READ", appearanceType: "M", solicitor: "DP", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true },
  { id: "30", date: "Tuesday 14", court: "Melbourne Children’s Court", clientName: "Tiarni KHAN-DUGGAN", appearanceType: "M", solicitor: "SP", notes: "", aidStatus: "Aid in place", aidNumber: "VLA-4029", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true },
  { id: "31", date: "Tuesday 14", court: "Melbourne Children’s Court", clientName: "Deklan MILLER", appearanceType: "M", solicitor: "CM", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: true, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true },
  { id: "32", date: "Tuesday 14", court: "Melbourne Children’s Court", clientName: "Levi FOLETTI", appearanceType: "App to vary bail", solicitor: "HB", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true },

  // Wednesday 15
  { id: "33", date: "Wednesday 15", court: "Bairnsdale Magistrates’ Court", clientName: "Chantelle HUMPHRIES", appearanceType: "FM", solicitor: "JO", notes: "", aidStatus: "Aid in place", aidNumber: "VLA-3329", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "34", date: "Wednesday 15", court: "Bendigo Magistrates’ Court", clientName: "Burt SMALLWOOD", appearanceType: "PG", solicitor: "GJ", notes: "GJ appearing for SMALLWOOD and MCKELLAR", aidStatus: "Aid in place", aidNumber: "VLA-1192", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  
  // Thursday 16
  { id: "35", date: "Thursday 16", court: "Bendigo Magistrates’ Court", clientName: "Callum KING", appearanceType: "M", solicitor: "GJ", notes: "Mentions", aidStatus: "Aid in place", aidNumber: "VLA-8271", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "36", date: "Thursday 16", court: "Bendigo Magistrates’ Court", clientName: "Jamie McCUMBER", appearanceType: "M", solicitor: "GJ", notes: "Mentions", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "37", date: "Thursday 16", court: "Dandenong Magistrates’ Court", clientName: "Blake BOYLE-JOHNSON", appearanceType: "RFS", solicitor: "CMR", notes: "CMR advised will seek LTA to appear via Webex", aidStatus: "Aid in place", aidNumber: "VLA-9922", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "38", date: "Thursday 16", court: "Dromana Magistrates’ Court", clientName: "Taylor JOHNSTON", appearanceType: "D", solicitor: "JB", notes: "JB advised that appearance may not be required", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "39", date: "Thursday 16", court: "Korumburra Magistrates’ Court", clientName: "Cindy ROBERTSON", appearanceType: "M", solicitor: "JO", notes: "", aidStatus: "Aid in place", aidNumber: "VLA-1290", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "40", date: "Thursday 16", court: "Melbourne Magistrates’ Court", clientName: "Shannon HURREN", appearanceType: "RFS", solicitor: "JB", notes: "", aidStatus: "Aid in place", aidNumber: "VLA-4820", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "41", date: "Thursday 16", court: "Melbourne Magistrates’ Court", clientName: "Jason SPENCER", appearanceType: "M", solicitor: "JB", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "42", date: "Thursday 16", court: "Melbourne Magistrates’ Court", clientName: "Adam JAMES", appearanceType: "M", solicitor: "JB", notes: "DL Assistance", aidStatus: "Aid in place", aidNumber: "VLA-4482", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "43", date: "Thursday 16", court: "Mildura Children’s Court", clientName: "Alex-Jack WOODBERRY", appearanceType: "M", solicitor: "JM", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "44", date: "Thursday 16", court: "Mildura Koori Court", clientName: "", appearanceType: "VALS Date", solicitor: "JM", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "45", date: "Thursday 16", court: "Mildura Magistrates’ Court", clientName: "Daniel ESPAGNE", appearanceType: "H", solicitor: "JM", notes: "JM appearing for ESPAGNE, SMITH and WOODBERRY", aidStatus: "Aid in place", aidNumber: "VLA-2991", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "46", date: "Thursday 16", court: "Mildura Magistrates’ Court", clientName: "Leticia SMITH", appearanceType: "M", solicitor: "JM", notes: "JM appearing for ESPAGNE, SMITH and WOODBERRY", aidStatus: "Aid in place", aidNumber: "VLA-1100", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "47", date: "Thursday 16", court: "Mildura Magistrates’ Court", clientName: "Alex-Jack WOODBERRY", appearanceType: "M", solicitor: "JM", notes: "JM appearing for ESPAGNE, SMITH and WOODBERRY", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "48", date: "Thursday 16", court: "Neighbourhood Justice Centre", clientName: "Brandon WATSON", appearanceType: "M", solicitor: "PC", notes: "PC appearing remotely for clients", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "49", date: "Thursday 16", court: "Neighbourhood Justice Centre", clientName: "Mason FORBES", appearanceType: "M", solicitor: "PC", notes: "PC appearing remotely for clients", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "50", date: "Thursday 16", court: "Sale Magistrates’ Court", clientName: "Joey LOGAN", appearanceType: "M", solicitor: "NN", notes: "NN to advise if appearing", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "51", date: "Thursday 16", court: "Warrnambool Magistrates’ Court", clientName: "Daryl EDEN", appearanceType: "M", solicitor: "XF", notes: "XF appearing for EDEN and KEAN MORRIS", aidStatus: "Aid in place", aidNumber: "VLA-9924", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "52", date: "Thursday 16", court: "Warrnambool Magistrates’ Court", clientName: "Laura KEAN MORRIS", appearanceType: "M", solicitor: "XF", notes: "XF appearing for EDEN and KEAN MORRIS", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "53", date: "Thursday 16", court: "Wangaratta Magistrates’ Court", clientName: "", appearanceType: "VALS Date", solicitor: "MM", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "54", date: "Thursday 16", court: "Melbourne Children's Koori Court", clientName: "Sebastian NINKOVICH", appearanceType: "PG", solicitor: "CM", notes: "", aidStatus: "Aid in place", aidNumber: "VLA-0012", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true },
  { id: "55", date: "Thursday 16", court: "Melbourne Magistrates' Court", clientName: "Tyrell DALTON-HOOD", appearanceType: "FM", solicitor: "PR", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true },

  // Friday 17
  { id: "56", date: "Friday 17", court: "Broadmeadows Magistrates’ Court", clientName: "Pamela MURPHY-KENNEALLY", appearanceType: "M", solicitor: "NN", notes: "NN appearing for MURPHY-KENNEALL", aidStatus: "Aid in place", aidNumber: "VLA-2993", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "57", date: "Friday 17", court: "Dandenong Koori Court", clientName: "", appearanceType: "VALS Date", solicitor: "VACANT", notes: "no capacity to send soli, no clients", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "58", date: "Friday 17", court: "Geelong Koori Court", clientName: "", appearanceType: "VALS Date", solicitor: "DE", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "59", date: "Friday 17", court: "Melbourne County Court", clientName: "Richard HAGEN/HAGAN", appearanceType: "SI", solicitor: "NN", notes: "David De Witt briefed for HAGEN/HAGAN", aidStatus: "Aid in place", aidNumber: "VLA-8822", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "60", date: "Friday 17", court: "Melbourne Magistrates’ Court", clientName: "Trevor HAINES", appearanceType: "PG", solicitor: "CC", notes: "", aidStatus: "Aid in place", aidNumber: "VLA-1188", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "61", date: "Friday 17", court: "Melbourne Magistrates’ Court", clientName: "*Samuel PURVIS", appearanceType: "M", solicitor: "NN", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "62", date: "Friday 17", court: "Melbourne Magistrates’ Court", clientName: "Kayla CLANCY", appearanceType: "CM", solicitor: "NN", notes: "", aidStatus: "Aid in place", aidNumber: "VLA-9928", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "63", date: "Friday 17", court: "Melbourne Magistrates’ Court", clientName: "*Karim KEENAN", appearanceType: "FM", solicitor: "NN", notes: "DL assistance", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "64", date: "Friday 17", court: "Melbourne Magistrates’ Court", clientName: "Kai MILONE", appearanceType: "M", solicitor: "SHO", notes: "", aidStatus: "Aid in place", aidNumber: "VLA-0044", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "65", date: "Friday 17", court: "Mildura Magistrates’ Court", clientName: "Guy MITCHELL", appearanceType: "M", solicitor: "JM", notes: "JM to advise if appearing/adjourning/briefing", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "66", date: "Friday 17", court: "Mildura Magistrates’ Court", clientName: "Tyrone WAKEFIELD", appearanceType: "FME", solicitor: "JM", notes: "JM to advise if appearing/adjourning/briefing", aidStatus: "Aid in place", aidNumber: "VLA-1029", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "67", date: "Friday 17", court: "Sale Magistrates Court", clientName: "Joey LOGAN", appearanceType: "M", solicitor: "NN", notes: "NN to advise if appearing/adjourning", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "68", date: "Friday 17", court: "Shepparton Koori Court", clientName: "Kaitlyn/Katelyn HATTON", appearanceType: "PG", solicitor: "CMR", notes: "", aidStatus: "Aid in place", aidNumber: "VLA-9923", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "69", date: "Friday 17", court: "Shepparton Magistrates’ Court", clientName: "Graham STEWART", appearanceType: "FM", solicitor: "CS", notes: "STEWART admin adj to 2.10.2026", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "70", date: "Friday 17", court: "Shepparton Magistrates’ Court", clientName: "Aaron BRIGGS", appearanceType: "M", solicitor: "MM", notes: "", aidStatus: "Aid in place", aidNumber: "VLA-8812", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "71", date: "Friday 17", court: "Shepparton Magistrates’ Court", clientName: "Shane CROWE", appearanceType: "M&PG", solicitor: "CS", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "72", date: "Friday 17", court: "Wangaratta Koori Court", clientName: "", appearanceType: "VALS Date", solicitor: "MM", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "73", date: "Friday 17", court: "Wangaratta Magistrates’ Court", clientName: "Dontae CLARK", appearanceType: "PG/CME", solicitor: "CC", notes: "CC to advise if appearing/adjourning/briefing", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "74", date: "Friday 17", court: "Warrnambool Koori Court", clientName: "Karla KING", appearanceType: "PG", solicitor: "XF", notes: "XF appearing for KING, CLARK-LESLIE, CLARKE and COUZENS", aidStatus: "Aid in place", aidNumber: "VLA-1122", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "75", date: "Friday 17", court: "Warrnambool Koori Court", clientName: "Kalliuda CLARK-LESLIE", appearanceType: "PG", solicitor: "XF", notes: "XF appearing for KING, CLARK-LESLIE, CLARKE and COUZENS", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "76", date: "Friday 17", court: "Warrnambool Koori Court", clientName: "Beau CLARKE", appearanceType: "CM", solicitor: "XF", notes: "XF appearing for KING, CLARK-LESLIE, CLARKE and COUZENS", aidStatus: "Aid in place", aidNumber: "VLA-9921", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "77", date: "Friday 17", court: "Warrnambool Koori Court", clientName: "Beau COUZENS", appearanceType: "B", solicitor: "XF", notes: "XF appearing for KING, CLARK-LESLIE, CLARKE and COUZENS", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: false },
  { id: "78", date: "Friday 17", court: "Broadmeadows Children’s Court", clientName: "Viviarnah GRIGGS", appearanceType: "M", solicitor: "SP", notes: "", aidStatus: "Aid in place", aidNumber: "VLA-0994", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true },
  { id: "79", date: "Friday 17", court: "Broadmeadows Children’s Court", clientName: "Viviarnah GRIGGS", appearanceType: "RoD", solicitor: "CM", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true },
  { id: "80", date: "Friday 17", court: "Broadmeadows Children’s Court", clientName: "Maddison EDWARDS", appearanceType: "M", solicitor: "TS", notes: "", aidStatus: "Aid in place", aidNumber: "VLA-0999", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true },
  { id: "81", date: "Friday 17", court: "Broadmeadows Children’s Court", clientName: "Cosmo NELSON BONVINO", appearanceType: "M", solicitor: "HLB", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true },
  { id: "82", date: "Friday 17", court: "Dandenong Children’s Court", clientName: "Hayley COCKER", appearanceType: "RoD", solicitor: "HB", notes: "", aidStatus: "Aid in place", aidNumber: "VLA-2994", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true },
  { id: "83", date: "Friday 17", court: "Dandenong Children’s Court", clientName: "Tate HUGHES", appearanceType: "M", solicitor: "ET", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true },
  { id: "84", date: "Friday 17", court: "Dandenong Children’s Court", clientName: "Max PARKER", appearanceType: "M", solicitor: "ET", notes: "", aidStatus: "Aid in place", aidNumber: "VLA-8824", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true },
  { id: "85", date: "Friday 17", court: "Dandenong Children’s Court", clientName: "Traeh COLEMAN-GIBSON", appearanceType: "M", solicitor: "HLB", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true },
  { id: "86", date: "Friday 17", court: "Dandenong Children’s Court", clientName: "Tynan SABAU", appearanceType: "Mention", solicitor: "CM", notes: "", aidStatus: "Aid in place", aidNumber: "VLA-8819", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true },
  { id: "87", date: "Friday 17", court: "Melbourne Children’s Court", clientName: "James ATKINSON", appearanceType: "M", solicitor: "TS", notes: "", aidStatus: "Aid not in place", aidNumber: "", solicitorConfirmed: false, budgetApprovalSought: false, otherStatusText: "", isBalitNgulu: true }
];

const createFeeRow = (amount = "") => ({
  id: globalThis.crypto?.randomUUID?.() || `fee_${Date.now()}_${Math.random().toString(36).slice(2)}`,
  amount,
});

const formatAppearanceFees = (fees = []) => fees
  .map((fee) => Number.parseFloat(fee?.amount))
  .filter(Number.isFinite)
  .map((amount) => amount.toLocaleString("en-AU", { style: "currency", currency: "AUD" }))
  .join("; ");

const fundingNeedsAidApplication = (status = "") => !["Aid in place", "In house", "Budget"].includes(status);

const COURT_LIST = [
  "Ballarat Magistrates’ Court",
  "Colac Magistrates’ Court",
  "Horsham Children’s Court",
  "Kyneton Magistrates’ Court",
  "Melbourne Koori Court",
  "Melbourne Magistrates’ Court",
  "Warrnambool County Court",
  "Wyndham Children’s Court",
  "Benalla Magistrates’ Court",
  "Echuca Magistrates’ Court",
  "Frankston Magistrates’ Court",
  "Geelong Magistrates’ Court",
  "Melbourne County Court",
  "Mildura Magistrates’ Court",
  "Moorabbin Magistrates’ Court",
  "Portland Koori Court",
  "Sale Magistrates’ Court",
  "Wyndham Magistrates’ Court",
  "Echuca Children’s Court",
  "Melbourne Children’s Court",
  "VCAT",
  "Other"
];

const SOLICITOR_LIST = [
  "JO", "DE", "DL", "LT", "MM", "LF", "JV", "GJ", "PR", "BM", "CMR", "TS", "DP", "ET", "HLB", "RG", "JM", "SHO", "XF", "TWB",
  "PC", "CS", "GW", "SP", "CM", "HB", "NN", "CC", "JB", "NM", "AM", "BN"
];

// firm: whose diary this is - VALS, or EAS, whose weekly diary is in the same format.
// Each firm's weeks are saved under that firm (src/lib/diaryFirms.js).
export default function VALSDiary({ isPortalMode = false, firm = "vals" }) {
  const firmInfo = diaryFirm(firm);
  const firmName = firmInfo.name;
  const fileInputRef = useRef(null);
  const archiveInputRef = useRef(null);
  const [entries, setEntries] = useState(() => {
    const saved = clientStorage.getItem(firmInfo.entriesKey);
    let parsed = saved ? JSON.parse(saved) : [];
    return parsed.map(normalizeValsDiaryEntry);
  });

  const [selectedDay, setSelectedDay] = useState("All Days");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sourceFileName, setSourceFileName] = useState(() => clientStorage.getItem(firmInfo.sourceFileKey) || "");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [isSavingWeek, setIsSavingWeek] = useState(false);

  // The week lives in the portal, under the firm, the same way every other tool's work does.
  // The browser copy below it is only what this tab happens to be showing.
  const { drafts: weekRows, fetchDrafts, saveDraft, deleteDraft } = useCloudDrafts();
  const [openWeekId, setOpenWeekId] = useState("");
  const [savedAt, setSavedAt] = useState("");
  const [isLoadingWeeks, setIsLoadingWeeks] = useState(true);
  const [showSavedWeeks, setShowSavedWeeks] = useState(false);
  const [deletingWeekId, setDeletingWeekId] = useState("");
  // What the portal already holds, so an edit that changes nothing does not save.
  const savedSnapshotRef = useRef("");
  const entriesRef = useRef(entries);
  const savedWeeks = useMemo(() => weekSummaries(weekRows), [weekRows]);
  // An appearance opened from the morning summary, scrolled to once the week is in.
  const focus = usePortalFocus("diary-entry", firm);
  useScrollToFocus(focus?.id, !isLoadingWeeks);

  const days = useMemo(() => [
    "All Days",
    ...Array.from(new Set(entries.map((entry) => entry.date).filter(Boolean)))
      .sort((a, b) => valsDiaryDaySortValue(a) - valsDiaryDaySortValue(b)),
  ], [entries]);

  // Modal / Form state for Add/Edit
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formState, setFormState] = useState({
    date: "Monday 13",
    court: "Melbourne Magistrates’ Court",
    clientName: "",
    appearanceType: "Mention",
    solicitor: "LF",
    appearing: "",
    appearingIsBarrister: false,
    appearanceFees: [createFeeRow()],
    notes: "",
    aidStatus: "In house",
    aidNumber: "",
    aidApplicationOnFile: false,
    budgetPaymentApprovedByML: false,
    otherStatusText: "",
    isBalitNgulu: false
  });

  // Save to clientStorage whenever entries change
  useEffect(() => {
    clientStorage.setItem(firmInfo.entriesKey, JSON.stringify(entries));
    entriesRef.current = entries;
  }, [entries]);

  useEffect(() => {
    if (sourceFileName) clientStorage.setItem(firmInfo.sourceFileKey, sourceFileName);
    else clientStorage.removeItem(firmInfo.sourceFileKey);
  }, [sourceFileName]);

  // Compute statistics
  const stats = useMemo(() => {
    const total = entries.length;
    const aidInPlace = entries.filter(e => e.aidStatus === "Aid in place").length;
    const aidNotApplied = entries.filter(e => e.aidStatus === "Aid not applied for").length;
    const aidPending = entries.filter(e => e.aidStatus === "Aid pending").length;
    const actionRequired = entries.filter(e =>
      (fundingNeedsAidApplication(e.aidStatus) && !e.aidApplicationOnFile)
      || (e.aidStatus === "Budget" && !e.budgetPaymentApprovedByML)
    ).length;

    return { total, aidInPlace, aidNotApplied, aidPending, actionRequired };
  }, [entries]);

  // Extensions and new grants due, worked out from the funding status and what each matter is
  // listed for. "Mark as applied" is kept on the entry, so it is saved with the week.
  const grantReminders = useMemo(() => diaryReminders(entries, { source: firmInfo.brand }), [entries, firmInfo.brand]);
  // The firm's Aid Planner matters, to fill each uploaded row's funding status from.
  const [aidMatters, setAidMatters] = useState(() => listLocalMatters(firmInfo.brand));
  useEffect(() => {
    let active = true;
    syncMattersWithPortal(firmInfo.brand).finally(() => { if (active) setAidMatters(listLocalMatters(firmInfo.brand)); });
    return () => { active = false; };
  }, []);
  const aidSuggestions = useMemo(
    () => valsDiarySuggestions(entries, aidMatters, { availabilityFor: getClaimAvailabilityTable }),
    [entries, aidMatters],
  );
  useEffect(() => {
    setEntries((current) => fillValsDiaryFromAidPlanner(current, aidSuggestions));
  }, [aidSuggestions]);
  const applyAidSuggestion = (id, suggestion) => {
    setEntries((prev) => prev.map((e) => (e.id === id
      ? { ...e, ...valsFillFields(e, suggestion), grantActionLodged: false, grantActionLodgedAt: "" }
      : e)));
  };

  const handleMarkGrantApplied = (reminder) => {
    setEntries(prev => prev.map(e => e.id === reminder.entryId
      ? {
          ...e,
          grantActionLodged: true,
          grantActionLodgedAt: new Date().toISOString(),
          aidApplicationOnFile: reminder.kind === "new_grant" && fundingNeedsAidApplication(e.aidStatus) ? true : e.aidApplicationOnFile,
        }
      : e));
    toast.success(`${reminder.clientName} marked as applied`);
  };

  // Filter entries
  const filteredEntries = useMemo(() => {
    return entries.filter(entry => {
      if (selectedDay !== "All Days" && entry.date !== selectedDay) return false;

      if (statusFilter !== "All" && statusFilter !== "ActionRequired" && entry.aidStatus !== statusFilter) return false;
      if (statusFilter === "ActionRequired") {
        const needsAction = (fundingNeedsAidApplication(entry.aidStatus) && !entry.aidApplicationOnFile)
          || (entry.aidStatus === "Budget" && !entry.budgetPaymentApprovedByML);
        if (!needsAction) return false;
      }

      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const clientMatch = entry.clientName?.toLowerCase().includes(query);
        const courtMatch = entry.court?.toLowerCase().includes(query);
        const solMatch = entry.solicitor?.toLowerCase().includes(query);
        const appearingMatch = entry.appearing?.toLowerCase().includes(query);
        const notesMatch = entry.notes?.toLowerCase().includes(query);
        const typeMatch = entry.appearanceType?.toLowerCase().includes(query);
        return clientMatch || courtMatch || solMatch || appearingMatch || notesMatch || typeMatch;
      }

      return true;
    });
  }, [entries, selectedDay, statusFilter, searchQuery]);

  const groupedEntries = useMemo(() => {
    const groups = {};
    filteredEntries.forEach(entry => {
      const date = entry.date;
      if (!groups[date]) groups[date] = { crime: {}, balit: {} };
      
      const sectionKey = entry.isBalitNgulu ? "balit" : "crime";
      const court = entry.court || "Unknown Court";
      if (!groups[date][sectionKey][court]) {
        groups[date][sectionKey][court] = [];
      }
      groups[date][sectionKey][court].push(entry);
    });

    const sortedDates = Object.keys(groups).sort((a, b) => {
      return valsDiaryDaySortValue(a) - valsDiaryDaySortValue(b);
    });

    return sortedDates.map(date => {
      const crimeCourts = Object.keys(groups[date].crime).sort().map(court => ({
        court,
        items: groups[date].crime[court]
      }));
      const balitCourts = Object.keys(groups[date].balit).sort().map(court => ({
        court,
        items: groups[date].balit[court]
      }));
      return { date, crimeCourts, balitCourts };
    });
  }, [filteredEntries]);

  const handleDiaryUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please choose a PDF diary.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Please choose a PDF smaller than 10 MB.");
      return;
    }
    if (entries.length && !window.confirm("Replace the diary currently on screen with this weekly PDF? Save the current week first if you need to keep it.")) {
      return;
    }

    setIsUploading(true);
    setUploadError("");
    try {
      const parsed = await parseValsDiaryFileStandalone(file);
      const importedEntries = mapParsedDiaryEntries(parsed?.entries || []);
      if (!importedEntries.length) {
        throw new Error("No diary entries could be read from this PDF. Please check that it is the weekly court diary and try again.");
      }
      setEntries(importedEntries);
      setSourceFileName(file.name);
      setSelectedDay("All Days");
      setSearchQuery("");
      setStatusFilter("All");
      entriesRef.current = importedEntries;
      toast.success(`Loaded ${importedEntries.length} entries from ${file.name}`);
      // A newly uploaded week becomes the week the portal holds, so edits from here are kept
      // without anyone having to remember to save.
      await persistWeek({
        id: null,
        silent: true,
        week: { entries: importedEntries, sourceFileName: file.name },
      });
    } catch (error) {
      const message = error?.message || "The diary PDF could not be processed.";
      setUploadError(message);
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  const buildWeekName = () => weekNameFor(entries, sourceFileName, firmName);

  // Saving the week to the portal. An id updates that week; without one a new week is filed
  // under the firm, so the diary reads the same from the firm's page and the admin page.
  // The week is passed in rather than read from state: a save that follows an upload or an
  // opened file runs before React has re-rendered, and the stale week is the wrong one.
  const persistWeek = async ({ id, silent = false, week } = {}) => {
    const currentEntries = week?.entries || entriesRef.current;
    const currentSourceFile = week ? week.sourceFileName || "" : sourceFileName;
    if (!currentEntries.length) {
      if (!silent) toast.error("Upload or add diary entries before saving this week.");
      return null;
    }
    const archive = weekArchive({
      weekName: weekNameFor(currentEntries, currentSourceFile, firmName),
      sourceFileName: currentSourceFile,
      entries: currentEntries,
      savedAt: new Date().toISOString(),
    });
    // Said here rather than letting the server answer in its own words about drafts.
    if (weekTooLarge(archive)) {
      toast.error("This week is too large to save in the portal. Save a copy to your computer and remove some entries.");
      return null;
    }
    setIsSavingWeek(true);
    try {
      const saved = await saveDraft({
        id: id || undefined,
        formType: VALS_DIARY_FORM_TYPE,
        draftName: archive.weekName,
        brand: firmInfo.brand,
        formData: archive,
      });
      savedSnapshotRef.current = JSON.stringify({
        entries: currentEntries,
        sourceFileName: currentSourceFile,
      });
      setOpenWeekId(saved?.id || id || "");
      setSavedAt(saved?.updated_at || archive.savedAt);
      if (!silent) toast.success(`Saved "${archive.weekName}" in the portal.`);
      return saved;
    } catch (error) {
      // Never silent: believing a week saved and finding it gone is the fault this replaces.
      toast.error(error?.message || "This week could not be saved in the portal.");
      return null;
    } finally {
      setIsSavingWeek(false);
    }
  };

  // Open the week last worked on, so the diary comes back as it was left rather than empty.
  // Anything already on screen in this tab wins: a reload should not discard unsaved edits.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const rows = await fetchDrafts(VALS_DIARY_FORM_TYPE, firmInfo.brand);
      if (cancelled) return;
      const latest = pickLatestWeek(rows);
      const archive = latest ? readWeekArchive(latest.form_data) : null;
      if (archive?.entries?.length && !entriesRef.current.length) {
        const restored = archive.entries.map(normalizeValsDiaryEntry);
        setEntries(restored);
        setSourceFileName(archive.sourceFileName || archive.weekName || "");
        setOpenWeekId(latest.id);
        setSavedAt(latest.updated_at || archive.savedAt || "");
        savedSnapshotRef.current = JSON.stringify({
          entries: restored,
          sourceFileName: archive.sourceFileName || archive.weekName || "",
        });
      }
      setIsLoadingWeeks(false);
    })();
    return () => { cancelled = true; };
  }, [fetchDrafts]);

  // Keep the open week current. Ticking a funding status is an edit like any other, and the
  // old browser-only diary lost exactly those when the tab closed.
  useEffect(() => {
    if (!openWeekId || !entries.length) return undefined;
    if (JSON.stringify({ entries, sourceFileName }) === savedSnapshotRef.current) return undefined;
    const timer = setTimeout(() => { persistWeek({ id: openWeekId, silent: true }); }, 1500);
    return () => clearTimeout(timer);
  }, [entries, sourceFileName, openWeekId]);

  const hasUnsavedChanges = Boolean(
    entries.length
      && JSON.stringify({ entries, sourceFileName }) !== savedSnapshotRef.current,
  );

  const handleSaveWeek = () => persistWeek({ id: openWeekId });

  const handleOpenSavedWeek = async (weekId) => {
    const row = weekRows.find((week) => week.id === weekId);
    const archive = row ? readWeekArchive(row.form_data) : null;
    if (!archive) {
      toast.error("That week could not be opened.");
      return;
    }
    if (hasUnsavedChanges && !window.confirm("Open a different week? The diary on screen has changes that have not been saved.")) return;
    const restored = archive.entries.map(normalizeValsDiaryEntry);
    const name = archive.sourceFileName || archive.weekName || "";
    setEntries(restored);
    setSourceFileName(name);
    setOpenWeekId(row.id);
    setSavedAt(row.updated_at || archive.savedAt || "");
    savedSnapshotRef.current = JSON.stringify({ entries: restored, sourceFileName: name });
    setSelectedDay("All Days");
    setSearchQuery("");
    setStatusFilter("All");
    setUploadError("");
    setShowSavedWeeks(false);
    toast.success(`Opened ${archive.weekName || "the saved week"}`);
  };

  const handleDeleteSavedWeek = async (weekId, name) => {
    if (!window.confirm(`Delete "${name}" from the portal? A copy saved to your computer is not affected.`)) return;
    setDeletingWeekId(weekId);
    try {
      await deleteDraft(weekId, VALS_DIARY_FORM_TYPE, firmInfo.brand);
      if (weekId === openWeekId) {
        // Still on screen, but no longer anywhere else: say so rather than autosaving it back.
        setOpenWeekId("");
        setSavedAt("");
        savedSnapshotRef.current = "";
      }
      toast.success("That week was deleted from the portal.");
    } catch (error) {
      toast.error(error?.message || "That week could not be deleted.");
    } finally {
      setDeletingWeekId("");
    }
  };

  // A copy on the person's own computer, which is not what the portal holds and does not
  // expire with it.
  const handleDownloadWeek = () => {
    if (!entries.length) {
      toast.error("Upload or add diary entries before saving this week.");
      return;
    }
    setIsSavingWeek(true);
    try {
      const archive = weekArchive({
        weekName: buildWeekName(),
        sourceFileName,
        entries,
        savedAt: new Date().toISOString(),
      });
      const blob = new Blob([JSON.stringify(archive, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${buildWeekName().replace(/[^a-z0-9_-]+/gi, "_")}.${firmInfo.brand}-week.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Weekly diary saved to your Downloads folder.");
    } catch (error) {
      toast.error(error?.message || "The week could not be saved.");
    } finally {
      setIsSavingWeek(false);
    }
  };

  const handleLoadSavedWeek = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const archive = readWeekFile(await file.text());
      if (entries.length && !window.confirm(`Replace the diary currently on screen with "${archive.weekName || file.name}"?`)) return;
      const restored = archive.entries.map(normalizeValsDiaryEntry);
      const name = archive.sourceFileName || archive.weekName || file.name;
      setEntries(restored);
      setSourceFileName(name);
      setSelectedDay("All Days");
      setSearchQuery("");
      setStatusFilter("All");
      setUploadError("");
      entriesRef.current = restored;
      toast.success(`Opened ${archive.weekName || file.name}`);
      // A week opened from a file is filed in the portal as a new week, so it is not left
      // sitting in this tab alone the way the whole diary used to be.
      await persistWeek({ id: null, silent: true, week: { entries: restored, sourceFileName: name } });
    } catch (error) {
      toast.error(error?.message || "The saved week could not be opened.");
    }
  };

  const handleOpenAdd = () => {
    setEditingEntry(null);
    setFormState({
      date: selectedDay !== "All Days" ? selectedDay : (days[1] || "Monday 13"),
      court: "Melbourne Magistrates’ Court",
      clientName: "",
      appearanceType: "Mention",
      solicitor: "LF",
      appearing: "",
      appearingIsBarrister: false,
      appearanceFees: [createFeeRow()],
      notes: "",
      aidStatus: "In house",
      aidNumber: "",
      aidApplicationOnFile: false,
      budgetPaymentApprovedByML: false,
      otherStatusText: "",
      isBalitNgulu: false
    });
    setIsFormOpen(true);
  };

  const handleOpenEdit = (entry) => {
    setEditingEntry(entry);
    const normalized = normalizeValsDiaryEntry(entry);
    setFormState({
      ...normalized,
      appearanceFees: normalized.appearanceFees.length ? normalized.appearanceFees : [createFeeRow()],
    });
    setIsFormOpen(true);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this appearance entry?")) {
      setEntries(prev => prev.filter(e => e.id !== id));
      toast.success("Entry deleted");
    }
  };

  const handleUpdateAidStatus = (id, status) => {
    setEntries(prev => prev.map(e => {
      if (e.id === id) {
        return {
          ...e,
          aidStatus: status,
          legalAidInPlace: status === "Aid in place",
          aidNumber: status === "Aid in place" ? e.aidNumber : "",
          aidApplicationOnFile: fundingNeedsAidApplication(status) ? e.aidApplicationOnFile : false,
          budgetPaymentApprovedByML: status === "Budget" ? e.budgetPaymentApprovedByML : false,
          otherStatusText: status === "Other" ? e.otherStatusText : "",
          grantActionLodged: false,
          grantActionLodgedAt: "",
          // Chosen by hand: the Aid Planner no longer fills it.
          aidStatusFromImport: false,
          aidPlannerFill: null
        };
      }
      return e;
    }));
  };

  const handleSaveForm = (e) => {
    e.preventDefault();
    if (formState.aidStatus === "Other" && !formState.otherStatusText.trim()) {
      toast.error("Please specify funding status details.");
      return;
    }

    const invalidFee = (formState.appearanceFees || []).some((fee) => fee.amount !== "" && (!Number.isFinite(Number(fee.amount)) || Number(fee.amount) < 0));
    if (invalidFee) {
      toast.error("Appearance fees must be valid amounts of $0 or more.");
      return;
    }
    const savedForm = normalizeValsDiaryEntry({
      ...formState,
      appearanceFees: (formState.appearanceFees || []).filter((fee) => fee.amount !== ""),
    });

    if (editingEntry) {
      setEntries(prev => prev.map(e => e.id === editingEntry.id ? { ...savedForm, id: e.id } : e));
      toast.success("Entry updated successfully");
    } else {
      const newEntry = {
        ...savedForm,
        id: String(Date.now())
      };
      setEntries(prev => [newEntry, ...prev]);
      toast.success("New entry added to diary");
    }
    setIsFormOpen(false);
  };

  const getEmailMailtoLink = (entry) => {
    const solEmail = firmInfo.staffEmails[entry.solicitor] || firmInfo.fallbackEmail;
    const clientName = entry.clientName || "General Slot";
    const subject = encodeURIComponent(`URGENT: Legal Aid Not in Place - ${clientName}`);
    const body = encodeURIComponent(
      `Hi ${entry.solicitor || "Team"},\n\n` +
      `I am reviewing the ${firmName} Court Diary for ${entry.date}.\n` +
      `For the appearance of ${clientName} at ${entry.court} (${entry.appearanceType || "Mention"}), the legal aid status is currently marked as "Aid not in place".\n\n` +
      `Could you please verify the aid status, or confirm if we need to request budget approval?\n\n` +
      `Thanks,\n${firmName} Coordinator`
    );

    return `mailto:${solEmail}?subject=${subject}&body=${body}`;
  };

  const handleExportExcel = () => {
    import("xlsx").then((XLSX) => {
      const rows = [
        [`${firmName} COURT DIARY & Funding Tracker`],
        [`Exported: ${new Date().toLocaleDateString()}`],
        [],
        ["Date", "Diary Section", "Court Location", "Client Name", "Listing Type", "Lawyer with Carriage", "Appearing", "Barrister Briefed", "Appearance Fee(s)", "Funding Status", "Aid App on File", "Budget Approved by ML", "Details / Reference", "Notes"]
      ];

      filteredEntries.forEach(e => {
        let details = "";
        if (e.aidStatus === "Aid in place") details = e.aidNumber || "";
        else if (e.aidStatus === "Other") details = e.otherStatusText || "";

        rows.push([
          e.date,
          e.isBalitNgulu ? "BALIT NGULU" : "Crime Team",
          e.court,
          e.clientName || "(No client / VALS Date)",
          e.appearanceType,
          e.solicitor,
          e.appearing || "To be confirmed",
          e.appearingIsBarrister ? "Yes" : "No",
          formatAppearanceFees(e.appearanceFees),
          e.aidStatus,
          fundingNeedsAidApplication(e.aidStatus) && e.aidApplicationOnFile ? "Yes" : "",
          e.aidStatus === "Budget" ? (e.budgetPaymentApprovedByML ? "Yes" : "No") : "",
          details,
          e.notes || ""
        ]);
      });

      const ws = XLSX.utils.aoa_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `${firmName}Diary`);
      
      ws["!cols"] = [
        { wch: 15 }, { wch: 15 }, { wch: 30 }, { wch: 25 }, { wch: 18 },
        { wch: 20 }, { wch: 24 }, { wch: 16 }, { wch: 20 }, { wch: 22 }, { wch: 16 }, { wch: 20 }, { wch: 25 }, { wch: 35 }
      ];

      XLSX.writeFile(wb, `${firmName}_Court_Diary_${selectedDay.replace(/\s+/g, "_")}.xlsx`);
      toast.success("Diary exported to Excel!");
    }).catch(err => {
      console.error(err);
      toast.error("Failed to load Excel library");
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const renderEntryRow = (entry) => {
    const actionRequired = (fundingNeedsAidApplication(entry.aidStatus) && !entry.aidApplicationOnFile)
      || (entry.aidStatus === "Budget" && !entry.budgetPaymentApprovedByML);
    const feeText = formatAppearanceFees(entry.appearanceFees);

    return (
      <tr key={entry.id} data-focus-id={entry.id} className={`diary-entry-row text-xs border-b border-slate-100 ${actionRequired ? "bg-red-50/40" : "bg-white"}`}>
        <td className="px-3 py-2.5 font-bold text-slate-900">
          {entry.clientName || <span className="font-normal italic text-slate-400">VALS Date Slot</span>}
        </td>
        <td className="px-3 py-2.5">
          <span className="inline-block rounded border border-purple-200 bg-purple-50 px-2 py-1 text-[10px] font-semibold text-purple-900">
            {entry.appearanceType || "—"}
          </span>
          <DiaryCodeNote text={entry.appearanceType} clientName={entry.clientName} className="no-print mt-1" />
        </td>
        <td className="px-3 py-2.5 text-center">
          <span className="inline-block rounded border border-blue-200 bg-blue-50 px-2 py-1 text-[10px] font-bold uppercase text-blue-900">
            {entry.solicitor || "—"}
          </span>
        </td>
        <td className="px-3 py-2.5">
          <span className={`inline-block rounded border px-2 py-1 text-[10px] font-semibold ${entry.appearingIsBarrister ? "border-pink-300 bg-pink-100 text-pink-900 italic" : entry.appearing ? "border-green-200 bg-green-50 text-green-900" : "border-slate-200 bg-slate-50 text-slate-500"}`}>
            {entry.appearing || "To be confirmed"}
          </span>
          {entry.appearingIsBarrister && <div className="mt-1 text-[9px] font-semibold text-pink-800">Barrister briefed</div>}
        </td>
        <td className="px-3 py-2.5 text-[10px] font-semibold text-amber-950">
          {feeText || <span className="font-normal italic text-slate-400">No fee entered</span>}
        </td>
        <td className="px-3 py-2.5 min-w-[170px]">
          <select
            value={entry.aidStatus || "Aid not applied for"}
            onChange={e => handleUpdateAidStatus(entry.id, e.target.value)}
            className="no-print w-full rounded border border-slate-300 bg-white px-2 py-1 text-[10px] font-medium focus:ring-1 focus:ring-teal-500"
          >
            {VALS_FUNDING_STATUSES.map(status => <option key={status} value={status}>{status}</option>)}
          </select>
          <div className="hidden print:block font-semibold text-slate-800">{entry.aidStatus}</div>
          {fundingNeedsAidApplication(entry.aidStatus) && entry.aidApplicationOnFile && (
            <div className="mt-1 text-[9px] font-medium text-green-700">Aid app on file: Yes</div>
          )}
          {entry.aidStatus === "Budget" && (
            <div className={`mt-1 text-[9px] font-medium ${entry.budgetPaymentApprovedByML ? "text-green-700" : "text-amber-700"}`}>
              {entry.budgetPaymentApprovedByML ? "Budget payment approved by ML" : "ML approval pending"}
            </div>
          )}
          {entry.aidStatus === "Aid in place" && entry.aidNumber && <div className="mt-1 text-[9px] font-medium text-green-700">{entry.aidNumber}</div>}
          {entry.aidStatus === "Other" && entry.otherStatusText && <div className="mt-1 text-[9px] text-slate-500">{entry.otherStatusText}</div>}
          {entry.grantActionLodged && <div className="mt-1 text-[9px] font-medium text-green-700">Extension / aid applied</div>}
          <AidPlannerHint entry={entry} suggestion={aidSuggestions.get(entry.id)} onUse={(suggestion) => applyAidSuggestion(entry.id, suggestion)} />
        </td>
        <td className="px-3 py-2.5 text-[10px] text-slate-600 max-w-[220px] break-words">
          {entry.notes || <span className="italic text-slate-300">—</span>}
        </td>
        <td className="px-3 py-2.5 text-center no-print">
          <div className="flex items-center justify-center gap-1.5">
            <button
              onClick={() => handleOpenEdit(entry)}
              className="p-1 hover:bg-slate-100 text-slate-500 hover:text-teal-700 rounded transition-colors"
              title="Edit"
            >
              <Edit2 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => handleDelete(entry.id)}
              className="p-1 hover:bg-slate-100 text-slate-500 hover:text-red-600 rounded transition-colors"
              title="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="vals-diary-print-root w-full min-h-screen bg-slate-50 font-sans p-6 text-slate-800">
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 6mm; }
          html, body { background: white !important; color: #0f172a !important; font-size: 9px; }
          body, body * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .no-print { display: none !important; }
          .print-card { box-shadow: none !important; border: 0.75px solid #94a3b8 !important; max-width: 100% !important; width: 100% !important; break-after: page; margin: 0 !important; border-radius: 0 !important; }
          .print-card:last-child { break-after: auto; }
          .print-card > div { padding: 4px 5px !important; }
          .print-card > div:first-child { padding: 5px 7px !important; }
          .print-card h3 { margin: 0 0 3px !important; font-size: 7px !important; }
          .print-card table { width: 100% !important; table-layout: fixed; font-size: 7px !important; line-height: 1.15 !important; }
          .print-card thead { display: table-header-group; }
          .print-card th, .print-card td { padding: 2px 3px !important; border-color: #cbd5e1 !important; vertical-align: top; overflow-wrap: anywhere; }
          .print-card .column-client { width: 16%; background: #ffedd5 !important; }
          .print-card .column-listing { width: 10%; background: #f3e8ff !important; }
          .print-card .column-lawyer { width: 11%; background: #dbeafe !important; }
          .print-card .column-appearing { width: 14%; background: #fce7f3 !important; }
          .print-card .column-fee { width: 12%; background: #fef3c7 !important; }
          .print-card .column-funding { width: 17%; background: #d1fae5 !important; }
          .court-location-row { background: #fef3c7 !important; break-after: avoid; }
          .diary-entry-row { break-inside: avoid; }
          .overflow-x-auto { overflow: visible !important; }
          .print-report-header { display: none !important; }
        }
      `}</style>

      {/* Header Panel */}
      <div className="max-w-7xl mx-auto mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 no-print">
        <div>
          <h1 className="text-2xl font-bold text-teal-800 flex items-center gap-2">
            <Calendar className="w-7 h-7 text-teal-600" />
            {firmName} Court Diary
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Provide appearance information, verify funding, and track approval or solicitor confirmation.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleDiaryUpload}
            className="hidden"
          />
          <input
            ref={archiveInputRef}
            type="file"
            accept=".json,.vals-week.json,application/json"
            onChange={handleLoadSavedWeek}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white font-medium px-4 py-2 rounded-lg text-sm transition-colors"
          >
            {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {isUploading ? "Reading PDF…" : "Upload New Week"}
          </button>

          <button
            onClick={handleSaveWeek}
            disabled={isSavingWeek || isUploading || !entries.length}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 border border-teal-700 bg-white hover:bg-teal-50 disabled:opacity-50 text-teal-800 font-medium px-4 py-2 rounded-lg text-sm transition-colors"
          >
            {isSavingWeek ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Week
          </button>

          <button
            onClick={handleDownloadWeek}
            disabled={isSavingWeek || isUploading || !entries.length}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50 text-slate-700 font-medium px-4 py-2 rounded-lg text-sm transition-colors"
          >
            <Download className="w-4 h-4 text-slate-500" />
            Save a Copy
          </button>

          <button
            onClick={handleOpenAdd}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 font-medium px-4 py-2 rounded-lg text-sm transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Entry
          </button>
          
          <button
            onClick={handleExportExcel}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 font-medium px-4 py-2 rounded-lg text-sm transition-colors"
          >
            <FileSpreadsheet className="w-4 h-4 text-green-600" />
            Export Excel
          </button>

          <button
            onClick={handlePrint}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 font-medium px-4 py-2 rounded-lg text-sm transition-colors"
          >
            <Printer className="w-4 h-4 text-slate-500" />
            Print Staff PDF
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto mb-6 no-print">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="min-w-0 lg:flex-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Current weekly diary</p>
            <p className="text-sm font-medium text-slate-800 truncate">
              {entries.length ? buildWeekName() : "No diary on screen — upload the latest weekly PDF to begin"}
            </p>
            {/* What is kept where. The diary used to live in this tab alone, so it is worth
                saying plainly that it no longer does. */}
            <p className="mt-1 flex items-center gap-1.5 text-xs font-medium">
              {isLoadingWeeks ? (
                <span className="flex items-center gap-1.5 text-slate-500">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Opening the week you last worked on…
                </span>
              ) : isSavingWeek ? (
                <span className="flex items-center gap-1.5 text-slate-500">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Saving…
                </span>
              ) : hasUnsavedChanges ? (
                <span className="flex items-center gap-1.5 text-amber-700">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Not saved yet — use Save Week
                </span>
              ) : savedAt ? (
                <span className="flex items-center gap-1.5 text-emerald-700">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Saved in the portal for {firmName} at {new Date(savedAt).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
                </span>
              ) : entries.length ? (
                <span className="flex items-center gap-1.5 text-amber-700">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Not saved yet — use Save Week
                </span>
              ) : null}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <p className="text-xs text-slate-500 sm:max-w-xs">
              Saved weeks are kept in the portal under {firmName}, so edits survive closing this tab and open again from any device. Save a Copy also keeps a file on this computer.
            </p>
            <button
              onClick={() => setShowSavedWeeks((open) => !open)}
              className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <Cloud className="w-4 h-4" />
              Saved Weeks{savedWeeks.length ? ` (${savedWeeks.length})` : ""}
            </button>
          </div>
        </div>

        {showSavedWeeks && (
          <div className="mt-3 bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold text-slate-800">Weeks saved in the portal</p>
              <button
                onClick={() => archiveInputRef.current?.click()}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 flex items-center gap-1.5"
              >
                <FolderOpen className="w-3.5 h-3.5" />
                Open a file from this computer
              </button>
            </div>

            {savedWeeks.length === 0 ? (
              <p className="mt-3 text-sm text-slate-500">
                No weeks saved yet. Upload a weekly PDF, or use Save Week to keep the diary on screen.
              </p>
            ) : (
              <ul className="mt-3 divide-y divide-slate-100">
                {savedWeeks.map((week) => (
                  <li key={week.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">
                        {week.weekName}
                        {week.id === openWeekId && (
                          <span className="ml-2 text-xs font-semibold text-teal-700">open now</span>
                        )}
                      </p>
                      <p className="text-xs text-slate-500">
                        {week.entryCount} {week.entryCount === 1 ? "entry" : "entries"}
                        {week.savedAt ? ` · saved ${new Date(week.savedAt).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}` : ""}
                        {week.savedBy ? ` · ${week.savedBy}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleOpenSavedWeek(week.id)}
                        disabled={week.id === openWeekId}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-40 text-slate-700"
                      >
                        Open
                      </button>
                      <button
                        onClick={() => handleDeleteSavedWeek(week.id, week.weekName)}
                        disabled={deletingWeekId === week.id}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40"
                        title={`Delete ${week.weekName}`}
                      >
                        {deletingWeekId === week.id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Trash2 className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {uploadError && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{uploadError}</span>
          </div>
        )}
      </div>

      {entries.length > 0 && (
        <div className="max-w-7xl mx-auto mb-6 no-print">
          <GrantRemindersPanel reminders={grantReminders} onMarkApplied={handleMarkGrantApplied} guideHref={firmInfo.hasGuides ? getPortalToolPath(firmInfo.brand, "reminders-guide") : ""} />
        </div>
      )}
      <div className="max-w-7xl mx-auto mb-6 no-print">
        <DiaryCodesLegend sheetHref={firmInfo.hasGuides ? getPortalToolPath(firmInfo.brand, "diary-codes") : ""} />
      </div>

      {/* Stats Summary Panel */}
      <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6 no-print">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
          <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Total Appearances</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{stats.total}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
          <p className="text-teal-700 text-xs font-semibold uppercase tracking-wider">Legal Aid in Place</p>
          <div className="flex justify-between items-baseline mt-1">
            <span className="text-2xl font-bold text-teal-800">{stats.aidInPlace}</span>
            <span className="text-xs bg-teal-100 text-teal-800 font-medium px-2 py-0.5 rounded-full">
              {stats.total ? Math.round((stats.aidInPlace / stats.total) * 100) : 0}%
            </span>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 border-l-4 border-l-red-500">
          <p className="text-red-700 text-xs font-semibold uppercase tracking-wider">Aid Not Applied For</p>
          <div className="flex justify-between items-baseline mt-1">
            <span className="text-2xl font-bold text-red-700">{stats.aidNotApplied}</span>
            <span className="text-xs bg-red-100 text-red-800 font-medium px-2 py-0.5 rounded-full">
              {stats.total ? Math.round((stats.aidNotApplied / stats.total) * 100) : 0}%
            </span>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 border-l-4 border-l-amber-500">
          <p className="text-amber-700 text-xs font-semibold uppercase tracking-wider">Aid Pending</p>
          <p className="text-2xl font-bold text-amber-700 mt-1">{stats.aidPending}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 border-l-4 border-l-green-500">
          <p className="text-green-700 text-xs font-semibold uppercase tracking-wider">Aid App Action Required</p>
          <p className="text-2xl font-bold text-green-700 mt-1">{stats.actionRequired}</p>
        </div>
      </div>

      {/* Filter and Search Panel */}
      <div className="max-w-7xl mx-auto bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-6 no-print flex flex-col gap-4">
        {/* Days Selector */}
        <div className="flex border-b border-slate-100 pb-2 overflow-x-auto gap-2">
          {days.map(day => (
            <button
              key={day}
              onClick={() => setSelectedDay(day)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                selectedDay === day 
                  ? "bg-teal-50 text-teal-800" 
                  : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
              }`}
            >
              {day}
            </button>
          ))}
        </div>

        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search client, court, or solicitor..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery("")} 
                className="absolute right-3 top-2.5 text-xs text-slate-400 hover:text-slate-700"
              >
                Clear
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto">
            <span className="text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">Filter Status:</span>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="border border-slate-300 rounded-lg text-sm p-2 focus:outline-none focus:ring-2 focus:ring-teal-500 w-full md:w-48"
            >
              <option value="All">All Appearances</option>
              {VALS_FUNDING_STATUSES.map(status => <option key={status} value={status}>{status}</option>)}
              <option value="ActionRequired">Aid app action required</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Print Header (only visible on print layout) */}
      <div className="print-report-header hidden print:block mb-4 border-b-2 border-teal-800 pb-3">
        <div className="flex items-start justify-between gap-6">
          <div>
            <h1 className="text-xl font-bold text-teal-900">{firmName} Weekly Court Diary</h1>
            <p className="mt-1 text-xs text-slate-600">Staff information-sharing report</p>
          </div>
          <div className="text-right text-[10px] text-slate-600">
            <p><strong>Week:</strong> {selectedDay === "All Days" ? days.filter(day => day !== "All Days").join(" · ") : selectedDay}</p>
            <p><strong>Source:</strong> {sourceFileName || "Manually prepared diary"}</p>
            <p><strong>Generated:</strong> {new Date().toLocaleString("en-AU")}</p>
          </div>
        </div>
        <p className="mt-2 text-[9px] text-slate-500">Court locations are highlighted in yellow. Funding information reflects the status recorded when this report was generated.</p>
      </div>

      {/* Grouped Diary Container */}
      <div className="vals-diary-print-content max-w-7xl mx-auto space-y-6">
        {groupedEntries.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-10 text-center text-slate-400 text-sm">
            No diary entries found matching the current filters.
          </div>
        ) : (
          groupedEntries.map(dateGroup => (
            <div key={dateGroup.date} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden print-card">
              {/* Date Header */}
              <div className="bg-teal-800 text-white px-4 py-3 font-bold flex justify-between items-center">
                <span className="text-base flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  {dateGroup.date}
                </span>
                <span className="text-xs bg-teal-700 px-2.5 py-1 rounded-full">
                  {dateGroup.crimeCourts.reduce((acc, cg) => acc + cg.items.length, 0) + dateGroup.balitCourts.reduce((acc, cg) => acc + cg.items.length, 0)} Entries
                </span>
              </div>

              {/* CRIME TEAM DIARY SECTION */}
              <div className="p-4 border-b border-slate-100 bg-slate-50/50">
                <h3 className="text-xs font-bold text-teal-800 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded bg-teal-600"></span>
                  Crime Team Diary
                </h3>
                {dateGroup.crimeCourts.length === 0 ? (
                  <p className="text-slate-400 text-xs italic pl-4">No Crime Team appearances scheduled.</p>
                ) : (
                  <div className="overflow-x-auto border border-slate-200 rounded-lg">
                    <table className="w-full text-left border-collapse bg-white">
                      <thead>
                        <tr className="text-slate-800 uppercase text-[9px] tracking-wider border-b border-slate-300">
                          <th className="column-client px-3 py-2 font-bold bg-orange-100">Client Name</th>
                          <th className="column-listing px-3 py-2 font-bold bg-purple-100">Listing Type</th>
                          <th className="column-lawyer px-3 py-2 font-bold bg-blue-100 text-center">Lawyer with Carriage</th>
                          <th className="column-appearing px-3 py-2 font-bold bg-pink-100">Appearing</th>
                          <th className="column-fee px-3 py-2 font-bold bg-amber-100">Appearance Fee</th>
                          <th className="column-funding px-3 py-2 font-bold bg-emerald-100">Funding Status</th>
                          <th className="px-3 py-2 font-bold bg-slate-100">Notes</th>
                          <th className="px-3 py-2 font-bold bg-slate-100 w-16 text-center no-print">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {dateGroup.crimeCourts.map(courtGroup => (
                          <React.Fragment key={courtGroup.court}>
                            <tr className="court-location-row bg-yellow-100 border-y border-yellow-300">
                              <td colSpan="8" className="px-3 py-2 font-bold text-amber-950 text-xs">
                                Court location: {courtGroup.court}
                              </td>
                            </tr>
                            {courtGroup.items.map(entry => renderEntryRow(entry))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* BALIT NGULU DIARY SECTION */}
              <div className="p-4 bg-slate-50/50">
                <h3 className="text-xs font-bold text-purple-800 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded bg-purple-600"></span>
                  BALIT NGULU DIARY
                </h3>
                {dateGroup.balitCourts.length === 0 ? (
                  <p className="text-slate-400 text-xs italic pl-4">No BALIT NGULU appearances scheduled.</p>
                ) : (
                  <div className="overflow-x-auto border border-slate-200 rounded-lg">
                    <table className="w-full text-left border-collapse bg-white">
                      <thead>
                        <tr className="text-slate-800 uppercase text-[9px] tracking-wider border-b border-slate-300">
                          <th className="column-client px-3 py-2 font-bold bg-orange-100">Client Name</th>
                          <th className="column-listing px-3 py-2 font-bold bg-purple-100">Listing Type</th>
                          <th className="column-lawyer px-3 py-2 font-bold bg-blue-100 text-center">Lawyer with Carriage</th>
                          <th className="column-appearing px-3 py-2 font-bold bg-pink-100">Appearing</th>
                          <th className="column-fee px-3 py-2 font-bold bg-amber-100">Appearance Fee</th>
                          <th className="column-funding px-3 py-2 font-bold bg-emerald-100">Funding Status</th>
                          <th className="px-3 py-2 font-bold bg-slate-100">Notes</th>
                          <th className="px-3 py-2 font-bold bg-slate-100 w-16 text-center no-print">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {dateGroup.balitCourts.map(courtGroup => (
                          <React.Fragment key={courtGroup.court}>
                            <tr className="court-location-row bg-yellow-100 border-y border-yellow-300">
                              <td colSpan="8" className="px-3 py-2 font-bold text-amber-950 text-xs">
                                Court location: {courtGroup.court}
                              </td>
                            </tr>
                            {courtGroup.items.map(entry => renderEntryRow(entry))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add / Edit Entry Dialog Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50 no-print">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[92vh] overflow-hidden border border-slate-200 flex flex-col">
            <div className="bg-teal-700 px-6 py-4 flex justify-between items-center text-white">
              <h2 className="font-bold text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {editingEntry ? "Edit Appearance Entry" : "Add Appearance Entry"}
              </h2>
              <button 
                onClick={() => setIsFormOpen(false)} 
                className="hover:bg-teal-800 p-1 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveForm} className="p-6 space-y-4 overflow-y-auto">
              {/* Date & Section */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Date / Day</label>
                  <select
                    value={formState.date}
                    onChange={e => setFormState(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    {days.filter(d => d !== "All Days").map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Diary Section</label>
                  <select
                    value={formState.isBalitNgulu ? "balit" : "crime"}
                    onChange={e => setFormState(prev => ({ ...prev, isBalitNgulu: e.target.value === "balit" }))}
                    className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="crime">Crime Team Diary</option>
                    <option value="balit">BALIT NGULU DIARY</option>
                  </select>
                </div>
              </div>

              {/* Court */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Court Location</label>
                <div className="flex gap-2">
                  <select
                    value={COURT_LIST.includes(formState.court) ? formState.court : "Other"}
                    onChange={e => {
                      const val = e.target.value;
                      setFormState(prev => ({ ...prev, court: val === "Other" ? "" : val }));
                    }}
                    className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    {COURT_LIST.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  {!COURT_LIST.includes(formState.court) && (
                    <input
                      type="text"
                      placeholder="Specify custom court..."
                      value={formState.court}
                      onChange={e => setFormState(prev => ({ ...prev, court: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Client Name */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Client Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Lydia LAUCH"
                    value={formState.clientName}
                    onChange={e => setFormState(prev => ({ ...prev, clientName: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <span className="text-xs text-slate-400">Leave blank for general slots.</span>
                </div>

                {/* Lawyer with carriage */}
                <div>
                  <label className="block text-xs font-semibold text-blue-800 uppercase tracking-wider mb-1">Lawyer with Carriage</label>
                  <select
                    value={formState.solicitor}
                    onChange={e => setFormState(prev => ({ ...prev, solicitor: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    {SOLICITOR_LIST.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Listing Type */}
                <div>
                  <label className="block text-xs font-semibold text-purple-800 uppercase tracking-wider mb-1">Listing Type</label>
                  <input
                    type="text"
                    placeholder="Diary code, e.g. MEN, PLEA, CMEN"
                    list="vals-diary-codes"
                    value={formState.appearanceType}
                    onChange={e => setFormState(prev => ({ ...prev, appearanceType: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <datalist id="vals-diary-codes">
                    {DIARY_CODES.map((entry) => <option key={entry.code} value={entry.code}>{entry.label}</option>)}
                  </datalist>
                  <DiaryCodeNote text={formState.appearanceType} className="mt-1" />
                </div>

                {/* Appearing */}
                <div>
                  <label className="block text-xs font-semibold text-pink-800 uppercase tracking-wider mb-1">Appearing</label>
                  <input
                    type="text"
                    placeholder="Name of person appearing, or leave blank"
                    value={formState.appearing || ""}
                    onChange={e => setFormState(prev => ({ ...prev, appearing: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <label className="mt-2 flex items-center gap-2 text-xs font-semibold text-pink-900 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formState.appearingIsBarrister || false}
                      onChange={e => setFormState(prev => ({ ...prev, appearingIsBarrister: e.target.checked }))}
                      className="w-4 h-4 accent-pink-600"
                    />
                    Barrister briefed (display in pink and italics)
                  </label>
                </div>
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-amber-950 uppercase tracking-wider">Appearance Fee</label>
                    <p className="mt-0.5 text-[11px] text-amber-800">Amount to be paid to counsel.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormState(prev => ({ ...prev, appearanceFees: [...(prev.appearanceFees || []), createFeeRow()] }))}
                    className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-bold text-amber-900 hover:bg-amber-100"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add more
                  </button>
                </div>
                <div className="space-y-2">
                  {(formState.appearanceFees || []).map((fee, index) => (
                    <div key={fee.id} className="flex items-center gap-2">
                      <span className="text-sm font-bold text-amber-900">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        aria-label={`Appearance fee ${index + 1}`}
                        placeholder="0.00"
                        value={fee.amount}
                        onChange={e => setFormState(prev => ({
                          ...prev,
                          appearanceFees: (prev.appearanceFees || []).map(item => item.id === fee.id ? { ...item, amount: e.target.value } : item),
                        }))}
                        className="w-full rounded-lg border border-amber-300 bg-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                      {(formState.appearanceFees || []).length > 1 && (
                        <button
                          type="button"
                          onClick={() => setFormState(prev => ({ ...prev, appearanceFees: prev.appearanceFees.filter(item => item.id !== fee.id) }))}
                          className="rounded p-2 text-slate-500 hover:bg-red-50 hover:text-red-700"
                          aria-label={`Remove appearance fee ${index + 1}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Notes</label>
                <input
                  type="text"
                  placeholder="Briefing or listing notes..."
                  value={formState.notes}
                  onChange={e => setFormState(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {/* Funding / Legal Aid Section */}
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Funding / Aid Status</label>
                  <select
                    value={formState.aidStatus || "Aid not applied for"}
                    onChange={e => {
                      const val = e.target.value;
                      setFormState(prev => ({ 
                        ...prev, 
                        aidStatus: val,
                        legalAidInPlace: val === "Aid in place",
                        aidApplicationOnFile: fundingNeedsAidApplication(val) ? prev.aidApplicationOnFile : false,
                        budgetPaymentApprovedByML: val === "Budget" ? prev.budgetPaymentApprovedByML : false,
                        aidNumber: val === "Aid in place" ? prev.aidNumber : "",
                        otherStatusText: val === "Other" ? prev.otherStatusText : "",
                        aidStatusFromImport: false,
                        aidPlannerFill: null
                      }));
                    }}
                    className="w-full border border-slate-300 bg-white rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 font-medium"
                  >
                    {VALS_FUNDING_STATUSES.map(status => <option key={status} value={status}>{status}</option>)}
                  </select>
                </div>

                {formState.aidStatus === "Aid in place" && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">VLA Aid Number</label>
                    <input
                      type="text"
                      placeholder="e.g. VLA-9821"
                      value={formState.aidNumber || ""}
                      onChange={e => setFormState(prev => ({ ...prev, aidNumber: e.target.value }))}
                      className="w-full border border-slate-300 bg-white rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  </div>
                )}

                {fundingNeedsAidApplication(formState.aidStatus) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs">
                    <p className="font-bold text-amber-950 mb-2">Aid application on file?</p>
                    <label className="flex items-center gap-2 select-none cursor-pointer font-bold text-amber-900">
                      <input
                        type="checkbox"
                        checked={formState.aidApplicationOnFile || false}
                        onChange={e => setFormState(prev => ({ ...prev, aidApplicationOnFile: e.target.checked }))}
                        className="w-4 h-4 accent-amber-700"
                      />
                      <span>{formState.aidApplicationOnFile ? "Yes — aid application is on file" : "No — aid application is not on file"}</span>
                    </label>
                  </div>
                )}

                {formState.aidStatus === "Budget" && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs">
                    <label className="flex items-center gap-2 select-none cursor-pointer font-bold text-blue-900">
                      <input
                        type="checkbox"
                        checked={formState.budgetPaymentApprovedByML || false}
                        onChange={e => setFormState(prev => ({ ...prev, budgetPaymentApprovedByML: e.target.checked }))}
                        className="w-4 h-4 accent-blue-700"
                      />
                      <span>Budget payment approved by ML</span>
                    </label>
                  </div>
                )}

                {formState.aidStatus === "Other" && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Specify Funding Details</label>
                    <input
                      type="text"
                      placeholder="e.g. Private, Pro bono, Grant"
                      value={formState.otherStatusText || ""}
                      onChange={e => setFormState(prev => ({ ...prev, otherStatusText: e.target.value }))}
                      className="w-full border border-slate-300 bg-white rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2 rounded-lg text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-teal-700 hover:bg-teal-800 text-white font-medium px-6 py-2 rounded-lg text-sm transition-colors shadow-sm"
                >
                  Save Entry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
